import { ResponsiveTable } from "../../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useState, useRef } from "react";
import { useApp } from "../../context/AppContext";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";

import { T } from "../../lib/i18n";
export default function DesignImport({ embedded }) {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    if (!file) return toast.error(zh ? "请先选择 Excel 文件" : "Choose an .xlsx");
    setBusy(true); setResult(null);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/design/import/preview", fd); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "preview failed"); }
    setBusy(false);
  };
  const doCommit = async () => {
    if (!file) return;
    if (!window.confirm(zh ? `确认导入 ${preview?.willCreate} 个新项目？重复地址将跳过。` : "Commit import?")) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/design/import/commit", fd); setResult(data); toast.success(`${zh ? "已导入" : "Imported"} ${data.created} / ${zh ? "跳过" : "skipped"} ${data.skipped}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "commit failed"); }
    setBusy(false);
  };

  return (
    <div className={embedded ? "" : "p-6 max-w-4xl mx-auto"} data-testid="design-import-page">
      {!embedded && <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 mb-1"><FileSpreadsheet className="h-6 w-6 text-emerald-600" />{zh ? "Excel 历史项目导入" : "Excel Import"}</h1>}
      <p className={`text-sm text-slate-500 ${embedded ? "mb-3 text-xs" : "mb-5"}`}>{zh ? "预览 → 字段映射 → 重复检测 → Dry Run → 正式导入。旧状态会映射到标准枚举并保留原值。" : "Preview → map → dedupe → dry-run → commit."}</p>

      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }} className="hidden" data-testid="design-import-input" />
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="design-import-choose"><UploadCloud className="h-4 w-4 mr-1" />{zh ? "选择 .xlsx" : "Choose"}</Button>
          <span className="text-sm text-slate-500">{file?.name || (zh ? "未选择文件" : "No file")}</span>
          <Button size="sm" onClick={doPreview} disabled={busy || !file} data-testid="design-import-preview-btn" className="ml-auto">{zh ? "预览 (Dry Run)" : "Preview"}</Button>
          {preview && <Button size="sm" onClick={doCommit} disabled={busy} data-testid="design-import-commit-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">{zh ? "正式导入" : "Commit"}</Button>}
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4" data-testid="design-import-preview">
          <div className="grid grid-cols-3 gap-3 mb-3 text-center">
            <Stat label={zh ? "解析行数" : "Rows"} v={preview.total} />
            <Stat label={zh ? "将创建" : "Will create"} v={preview.willCreate} color="text-emerald-600" />
            <Stat label={zh ? "重复(跳过)" : "Duplicates"} v={preview.duplicates.length} color="text-amber-600" />
          </div>
          {preview.exceptions.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 mb-3" data-testid="design-import-exceptions">
              <p className="text-xs font-semibold text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{zh ? "需人工确认" : "Needs review"} ({preview.exceptions.length})</p>
              {preview.exceptions.slice(0, 8).map((e, i) => <p key={i} className="text-[11px] text-amber-700">{e.address} — {T(e.reason)}</p>)}
            </div>
          )}
          <div className="max-h-72 overflow-auto text-xs">
            <ResponsiveTable className="w-full">
              <thead><tr className="text-left text-slate-400"><th className="py-1">{zh ? "地址" : "Address"}</th><th>US</th><th>{zh ? "阶段状态(原值→标准)" : "Stages"}</th></tr></thead>
              <tbody>{preview.preview.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 align-top" data-testid={`design-import-row-${i}`}>
                  <td className="py-1 pr-2">{r.address}</td><td className="pr-2">{r.usLead || "—"}</td>
                  <td className="text-[10px] text-slate-500">{Object.entries(r.stages).filter(([, v]) => v.original).slice(0, 6).map(([k, v]) => `${k}:${v.original}→${v.standard}`).join(" · ")}</td>
                </tr>
              ))}</tbody>
            </ResponsiveTable>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4" data-testid="design-import-result">
          <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" />{zh ? "导入完成" : "Done"}</p>
          <p className="text-sm text-slate-600">{zh ? "新建" : "Created"} {result.created} · {zh ? "跳过重复" : "Skipped"} {result.skipped} · {zh ? "异常" : "Exceptions"} {result.exceptions.length} · Batch {result.batchId}</p>
        </div>
      )}
    </div>
  );
}
function Stat({ label, v, color }) { return <div><p className={`text-2xl font-bold ${color || "text-slate-800"}`}>{v}</p><p className="text-[11px] text-slate-400">{label}</p></div>; }
